import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Briefcase,
  Check,
  ExternalLink,
  FileText,
  Link as LinkIcon,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  UserRound,
  X as XIcon,
} from "lucide-react";
import { API } from "service/Interceptor";
import { applicantDisplayName, serverFiles } from "utils/utils";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const STATUS_STYLE = {
  PENDING: "bg-zinc-800/80 text-zinc-300 border-zinc-700",
  SHORTLIST: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  SELECTED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  REJECTED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Section = ({ label, children }) => (
  <div>
    <div className="text-[0.65rem] uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-2">
      {label}
    </div>
    {children}
  </div>
);

const mapPreloadedBase = (preloaded) => {
  const links = Array.isArray(preloaded?.workLinks)
    ? preloaded.workLinks
    : Array.isArray(preloaded?.additionalLink)
      ? preloaded.additionalLink
      : [];
  return {
    firstName: preloaded?.firstName || preloaded?.name?.split?.(" ")?.[0] || "",
    lastName:
      preloaded?.lastName ||
      preloaded?.name?.split?.(" ")?.slice(1).join(" ") ||
      "",
    userId: preloaded?.userId,
    userAliasId: preloaded?.userAliasId || preloaded?.userId,
    email: preloaded?.email || null,
    mobileNo: preloaded?.mobileNo || null,
    profileUrl: preloaded?.profileUrl || null,
    applicationStatus:
      preloaded?.applicationStatus || preloaded?.status || "PENDING",
    createdAt: preloaded?.appliedAt || preloaded?.createdAt || null,
    additionalLink: links,
    portfolioUrl: preloaded?.portfolioUrl || null,
    note: preloaded?.note || preloaded?.message || null,
    industry: preloaded?.industry || [],
    skills: preloaded?.skills || [],
    workExperiences: preloaded?.workExperiences || [],
    city: preloaded?.city || null,
    state: preloaded?.state || null,
  };
};

const mergeProfileIntoApplicant = (base, profile) => {
  if (!profile) return base;
  const profileLinks = Array.isArray(profile.additionalLink)
    ? profile.additionalLink.filter(Boolean)
    : [];
  const applyLinks = Array.isArray(base.additionalLink)
    ? base.additionalLink.filter(Boolean)
    : [];
  const mergedLinks = [...new Set([...applyLinks, ...profileLinks])];

  return {
    ...base,
    firstName: profile.firstName || base.firstName,
    lastName: profile.lastName || base.lastName,
    userId: profile.userId || base.userId,
    userAliasId: profile.userAliasId || base.userAliasId,
    email: profile.email || base.email || null,
    mobileNo: profile.mobileNo || base.mobileNo || null,
    profileUrl: profile.profileUrl || base.profileUrl || null,
    portfolioUrl: base.portfolioUrl || profile.portfolioUrl || null,
    additionalLink: mergedLinks.length ? mergedLinks : base.additionalLink,
    industry:
      (Array.isArray(profile.industry) && profile.industry.length
        ? profile.industry
        : base.industry) || [],
    skills:
      (Array.isArray(profile.skills) && profile.skills.length
        ? profile.skills
        : base.skills) || [],
    workExperiences:
      (Array.isArray(profile.workExperiences) && profile.workExperiences.length
        ? profile.workExperiences
        : base.workExperiences) || [],
    city:
      profile.city ||
      (profile.cityName
        ? { id: profile.cityId, name: profile.cityName }
        : base.city),
    state:
      profile.state ||
      (profile.stateName
        ? { id: profile.stateId, name: profile.stateName }
        : base.state),
    applicationStatus: base.applicationStatus || profile.applicationStatus,
    createdAt: base.createdAt || profile.createdAt,
    note: base.note,
  };
};

const isJobApplicationNumber = (value) =>
  !!value &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value)
  );

const ApplicantDetailsModal = ({
  open,
  applicationNumber,
  projectId,
  postId = null,
  briefTitle,
  preloaded = null,
  seed = null,
  onClose,
  onShortlist,
  onSelect,
  onReject,
  actionBusy,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setData(null);
      setError("");
      return;
    }

    let cancelled = false;
    const listRow = seed || preloaded;
    const alias =
      listRow?.userAliasId ||
      listRow?.userId ||
      null;
    // Real JobApplication ids are UUIDs (brand apply + new creator jobs).
    // Comment ids are numeric — those are legacy post-only applies.
    const useBrandUserDetails = isJobApplicationNumber(applicationNumber);

    // Brand path (and creator jobs once they use JobApplication): email is on
    // the application snapshot via user-details — same as brand applicants page.
    if (useBrandUserDetails) {
      const base = listRow ? mapPreloadedBase(listRow) : null;
      if (base) {
        setData(base);
        setError("");
      }

      (async () => {
        setLoading(true);
        try {
          const res = await API.get(
            `api/jobs/${applicationNumber}/user-details`,
            { skipErrorToast: true }
          );
          const details = res?.data?.data || null;
          if (!cancelled) {
            setData(
              base ? mergeProfileIntoApplicant(base, details) : details
            );
            if (!details && !base) {
              setError("Could not load application details");
            }
          }
        } catch (err) {
          if (!cancelled && !base) {
            setData(null);
            setError(
              err?.response?.data?.message ||
                err?.message ||
                "Could not load application details"
            );
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    // Legacy creator post-comment applicants
    if (preloaded && postId && alias) {
      const base = mapPreloadedBase(preloaded);
      setData(base);
      setError("");

      (async () => {
        setLoading(true);
        try {
          const res = await API.get(
            `api/post/${postId}/comment/applicant/${alias}`,
            { skipErrorToast: true }
          );
          const details = res?.data?.data || null;
          if (!cancelled && details) {
            setData(mergeProfileIntoApplicant(base, details));
          }
        } catch {
          /* keep base */
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (preloaded || seed) {
      setData(mapPreloadedBase(preloaded || seed));
      setError("");
      setLoading(false);
      return;
    }

    if (!applicationNumber) {
      setData(null);
      setError("");
      return;
    }

    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await API.get(
            `api/jobs/${applicationNumber}/user-details`,
            { skipErrorToast: true }
          );
        if (!cancelled) setData(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Could not load application details"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, applicationNumber, preloaded, postId, seed]);

  const name = applicantDisplayName(data);
  const avatarSrc = data?.profileUrl ? serverFiles(data.profileUrl) : null;
  const statusKey = String(data?.applicationStatus || "PENDING").toUpperCase();
  const statusLabel =
    statusKey === "SHORTLIST"
      ? "shortlisted"
      : statusKey === "SELECTED"
        ? "selected"
        : statusKey.toLowerCase();
  const city = [data?.city?.name, data?.state?.name].filter(Boolean).join(", ");
  const workLinks = Array.isArray(data?.additionalLink)
    ? data.additionalLink.filter(Boolean)
    : [];
  const note = data?.note || null;
  const skills = Array.isArray(data?.skills)
    ? data.skills.filter(Boolean)
    : [];
  const industry = Array.isArray(data?.industry)
    ? data.industry.filter(Boolean)
    : [];
  const experience = Array.isArray(data?.workExperiences)
    ? data.workExperiences.slice(0, 3)
    : [];
  const isRejected = statusKey === "REJECTED";
  const isSelected = statusKey === "SELECTED";
  const isShortlisted = statusKey === "SHORTLIST";
  const profileAlias = data?.userAliasId || null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            className="relative w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/40 max-h-[92vh] overflow-hidden flex flex-col"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#6C63F5]/15 to-transparent" />

            <div className="relative flex items-start justify-between gap-3 px-6 pt-5 pb-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.16em] text-[#8b83ff] font-semibold">
                  Application
                </div>
                <h3 className="mt-1 font-display font-bold text-xl text-zinc-50 line-clamp-2">
                  {briefTitle || "Brief application"}
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-zinc-500 hover:text-zinc-200 h-9 w-9 rounded-full inline-flex items-center justify-center shrink-0"
                aria-label="Close"
              >
                <XIcon size={20} />
              </button>
            </div>

            <div className="relative flex-1 overflow-y-auto px-6 pb-6 space-y-5">
              {loading && !data ? (
                <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" /> Loading
                  details…
                </div>
              ) : error && !data ? (
                <div className="rounded-xl border border-dashed border-zinc-800 py-12 text-center text-sm text-zinc-500">
                  {error}
                </div>
              ) : !data ? (
                <div className="rounded-xl border border-dashed border-zinc-800 py-12 text-center text-sm text-zinc-500">
                  No details available.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={name}
                        className="h-14 w-14 rounded-full object-cover shrink-0 border border-zinc-800"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = NO_IMAGE;
                        }}
                      />
                    ) : (
                      <div className="avatar-fallback h-14 w-14 rounded-full shrink-0 flex items-center justify-center font-semibold">
                        {initials(name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-semibold text-zinc-50 truncate">
                          {name}
                        </p>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider ${
                            STATUS_STYLE[statusKey] || STATUS_STYLE.PENDING
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                        {data?.createdAt && (
                          <span>
                            Applied{" "}
                            {new Date(data.createdAt).toLocaleDateString()}
                          </span>
                        )}
                        {city && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin size={11} /> {city}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {data?.email ? (
                      <a
                        href={`mailto:${data.email}`}
                        className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-3.5 py-2.5 text-sm text-zinc-300 hover:border-zinc-600 transition-colors inline-flex items-center gap-2 min-w-0"
                      >
                        <Mail size={14} className="text-[#8b83ff] shrink-0" />
                        <span className="truncate">{data.email}</span>
                      </a>
                    ) : (
                      <div className="rounded-xl border border-dashed border-zinc-800 px-3.5 py-2.5 text-sm text-zinc-500 inline-flex items-center gap-2 min-w-0">
                        <Mail size={14} className="shrink-0" />
                        Email not available
                      </div>
                    )}
                    {data?.mobileNo ? (
                      <a
                        href={`tel:${data.mobileNo}`}
                        className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-3.5 py-2.5 text-sm text-zinc-300 hover:border-zinc-600 transition-colors inline-flex items-center gap-2"
                      >
                        <Phone size={14} className="text-[#8b83ff] shrink-0" />
                        {data.mobileNo}
                      </a>
                    ) : null}
                  </div>

                  {(industry.length > 0 || skills.length > 0) && (
                    <Section label="Looking for match">
                      <div className="flex flex-wrap gap-1.5">
                        {industry.map((item) => (
                          <span
                            key={`ind-${item}`}
                            className="inline-flex items-center gap-1 rounded-full border border-[#6C63F5]/25 bg-[#6C63F5]/10 px-2.5 py-1 text-[0.7rem] font-medium text-[#c9c5ff]"
                          >
                            <Briefcase size={11} /> {item}
                          </span>
                        ))}
                        {skills.slice(0, 8).map((item) => (
                          <span
                            key={`sk-${item}`}
                            className="inline-flex items-center rounded-full border border-zinc-700/80 bg-zinc-900/80 px-2.5 py-1 text-[0.7rem] font-medium text-zinc-300"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </Section>
                  )}

                  {note && (
                    <Section label="Note">
                      <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 px-4 py-3 text-sm text-zinc-200 whitespace-pre-wrap">
                        {note}
                      </div>
                    </Section>
                  )}

                  <Section label="Work links">
                    {workLinks.length > 0 ? (
                      <div className="space-y-2">
                        {workLinks.map((url) => (
                          <a
                            key={url}
                            href={
                              /^https?:\/\//i.test(url) ? url : `https://${url}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/70 px-4 py-3 text-sm text-zinc-200 hover:border-[#6C63F5]/50 transition-colors"
                          >
                            <LinkIcon
                              size={14}
                              className="text-[#8b83ff] shrink-0"
                            />
                            <span className="truncate flex-1">{url}</span>
                            <ExternalLink
                              size={13}
                              className="text-zinc-500 shrink-0"
                            />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-zinc-800 px-4 py-3 text-sm text-zinc-500">
                        No work links shared with this application.
                      </div>
                    )}
                  </Section>

                  <Section label="Portfolio">
                    {data?.portfolioUrl ? (
                      <a
                        href={
                          /^https?:\/\//i.test(data.portfolioUrl)
                            ? data.portfolioUrl
                            : serverFiles(data.portfolioUrl)
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 px-4 py-3 hover:border-[#6C63F5]/50 transition-colors"
                      >
                        <div className="h-10 w-10 rounded-lg bg-[#6C63F5]/15 flex items-center justify-center text-[#8b83ff] shrink-0">
                          <FileText size={18} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-zinc-100 font-medium">
                            Portfolio
                          </div>
                          <div className="text-xs text-[#8b83ff] inline-flex items-center gap-1">
                            Open / download <ExternalLink size={11} />
                          </div>
                        </div>
                      </a>
                    ) : (
                      <div className="rounded-xl border border-dashed border-zinc-800 px-4 py-3 text-sm text-zinc-500">
                        No portfolio attached.
                      </div>
                    )}
                  </Section>

                  {experience.length > 0 && (
                    <Section label="Experience">
                      <div className="space-y-2">
                        {experience.map((exp, i) => (
                          <div
                            key={exp?.id || i}
                            className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-3.5 py-2.5"
                          >
                            <p className="text-sm text-zinc-100 font-medium">
                              {exp?.role || exp?.jobTitle || exp?.title || "Role"}
                            </p>
                            <p className="text-xs text-zinc-500 mt-0.5">
                              {[exp?.companyName || exp?.company, exp?.duration]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </p>
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {profileAlias && (
                    <Link
                      to={`/user-profile/${profileAlias}`}
                      className="inline-flex items-center gap-1.5 text-sm text-[#8b83ff] hover:text-white font-medium"
                    >
                      <UserRound size={14} /> View full profile
                    </Link>
                  )}
                </>
              )}
            </div>

            {!loading && data && (
              <div className="relative border-t border-zinc-800/80 px-6 py-4 flex flex-wrap items-center gap-2 bg-zinc-950/95">
                <button
                  type="button"
                  disabled={
                    isShortlisted ||
                    isSelected ||
                    isRejected ||
                    actionBusy
                  }
                  onClick={() => onShortlist?.(data)}
                  className="rounded-full border border-zinc-700 px-3.5 py-2 text-xs font-semibold text-zinc-200 hover:border-[#6C63F5]/50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  <Check size={13} />
                  {isShortlisted ? "Shortlisted" : "Shortlist"}
                </button>
                <button
                  type="button"
                  disabled={isSelected || isRejected || actionBusy}
                  onClick={() => onSelect?.(data)}
                  className="rounded-full border border-zinc-700 px-3.5 py-2 text-xs font-semibold text-zinc-200 hover:border-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  <Sparkles size={13} />
                  {isSelected ? "Selected" : "Select"}
                </button>
                <button
                  type="button"
                  disabled={isRejected || actionBusy}
                  onClick={() => onReject?.(data)}
                  className="rounded-full border border-zinc-700 px-3.5 py-2 text-xs font-semibold text-zinc-300 hover:border-rose-500/40 hover:text-rose-300 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  <XIcon size={13} />
                  {isRejected ? "Rejected" : "Reject"}
                </button>
                {projectId && (
                  <span className="ml-auto text-[0.65rem] text-zinc-600 truncate max-w-[9rem]">
                    #{String(applicationNumber || "").slice(0, 8)}
                  </span>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ApplicantDetailsModal;
